/** Rich procedural WebAudio FX — no external files needed. */
export class AudioBus {
  muted = false;
  private ctx: AudioContext | null = null;

  constructor() {
    const stored = localStorage.getItem("exorunner_mute");
    this.muted = stored === "1";
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    localStorage.setItem("exorunner_mute", muted ? "1" : "0");
  }

  private ensure(): AudioContext | null {
    if (this.muted) return null;
    if (!this.ctx) {
      const Ctx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      this.ctx = new Ctx();
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    return this.ctx;
  }

  private tone(
    freq: number,
    dur: number,
    type: OscillatorType,
    gain: number,
    attack = 0.005,
    decay?: number,
  ): void {
    const ctx = this.ensure();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    const t = ctx.currentTime;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(gain, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + (decay ?? dur));
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + dur);
  }

  private sweep(
    startFreq: number,
    endFreq: number,
    dur: number,
    type: OscillatorType,
    gain: number,
  ): void {
    const ctx = this.ensure();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    const t = ctx.currentTime;
    osc.frequency.setValueAtTime(startFreq, t);
    osc.frequency.exponentialRampToValueAtTime(endFreq, t + dur);
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + dur);
  }

  private noise(dur: number, gain: number): void {
    const ctx = this.ensure();
    if (!ctx) return;
    const bufferSize = ctx.sampleRate * dur;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const g = ctx.createGain();
    const t = ctx.currentTime;
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    const filter = ctx.createBiquadFilter();
    filter.type = "highpass";
    filter.frequency.value = 3000;
    src.connect(filter);
    filter.connect(g);
    g.connect(ctx.destination);
    src.start(t);
    src.stop(t + dur);
  }

  jump(): void {
    this.sweep(280, 560, 0.12, "square", 0.06);
    this.tone(420, 0.06, "triangle", 0.03, 0.002);
    this.noise(0.04, 0.015);
  }

  collect(): void {
    this.tone(880, 0.08, "triangle", 0.05, 0.003);
    this.tone(1320, 0.1, "sine", 0.04, 0.01, 0.08);
    this.tone(1760, 0.12, "sine", 0.025, 0.02, 0.1);
  }

  hurt(): void {
    this.sweep(400, 80, 0.3, "sawtooth", 0.07);
    this.noise(0.15, 0.06);
    this.tone(60, 0.4, "square", 0.04, 0.01, 0.35);
  }

  ui(): void {
    this.tone(660, 0.06, "sine", 0.035, 0.003);
    this.tone(990, 0.04, "triangle", 0.02, 0.01);
  }

  /** Menu start button — ascending fanfare */
  start(): void {
    this.tone(440, 0.1, "square", 0.04, 0.005);
    setTimeout(() => this.tone(550, 0.1, "square", 0.04, 0.005), 80);
    setTimeout(() => this.tone(660, 0.12, "triangle", 0.05, 0.005), 160);
    setTimeout(() => this.tone(880, 0.18, "triangle", 0.05, 0.005), 250);
  }

  /** Score milestone ding */
  milestone(): void {
    this.tone(1200, 0.08, "sine", 0.04, 0.002);
    this.tone(1600, 0.1, "triangle", 0.03, 0.01, 0.08);
    this.noise(0.03, 0.01);
  }

  /** Power-up collected — triumphant rising arpeggio */
  powerUp(): void {
    this.tone(440, 0.08, "square", 0.04, 0.003);
    setTimeout(() => this.tone(660, 0.08, "square", 0.04, 0.003), 60);
    setTimeout(() => this.tone(880, 0.08, "triangle", 0.05, 0.003), 120);
    setTimeout(() => this.tone(1320, 0.15, "sine", 0.05, 0.005), 180);
  }

  /** Power-down collected — ominous descending buzz */
  powerDown(): void {
    this.sweep(600, 150, 0.3, "sawtooth", 0.05);
    this.noise(0.1, 0.03);
    setTimeout(() => this.tone(100, 0.2, "square", 0.03, 0.01), 100);
  }
}
